import { View } from "react-native"
import { type ReactNode } from "react"
import { BFBackButton } from "@/components/standard/BFBackButton"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { cn } from "@/components/util/cn"
import { useIsDesktop } from "@/hooks/useIsDesktop"
import { StatusBar } from "expo-status-bar"
import FullScreenBackgroundGradient from "@/components/backgrounds/FullScreenBackgroundGradient"

interface BFScreenProps {
  children: ReactNode
  headerSticky?: boolean
  noBackButton?: boolean
  bottomInsets?: boolean
  header?: ReactNode
  className?: string
  headerClassName?: string
  dark?: boolean
  gradientBackground?: boolean
  includeStandardSideMargins?: boolean
  ignoreTopSafeArea?: boolean
}

export const BFScreen = ({
  children,
  headerSticky = true,
  header,
  dark = false,
  headerClassName,
  noBackButton = false,
  bottomInsets = false,
  className,
  gradientBackground = false,
  includeStandardSideMargins = false,
  ignoreTopSafeArea = false,
}: BFScreenProps) => {
  const insets = useSafeAreaInsets()
  const isDesktop = useIsDesktop()

  // Always set ignoreSafeArea to false on Web
  const shouldIgnoreTopSafeArea = isDesktop ? false : ignoreTopSafeArea

  const resolvedBackgroundColor = (() => {
    if (gradientBackground) {
      return "transparent"
    }
    return dark ? "bg-midnight-blue" : "bg-off-white"
  })()

  const resolvedTopPadding = (() => {
    if (isDesktop) {
      return 0
    }
    if (ignoreTopSafeArea) {
      return insets.top
    }

    const constantTopPadding: number = 18
    return insets.top + constantTopPadding
  })()

  return (
    <>
      {gradientBackground && <FullScreenBackgroundGradient />}
      <View
        testID={"BFScreen"}
        className={cn("flex-1 size-full", resolvedBackgroundColor, className)}
        style={[
          bottomInsets ? { paddingBottom: insets.bottom } : {},
          shouldIgnoreTopSafeArea ? { marginTop: -insets.top } : {}, // Bleeds content outside safe area to top edge
          includeStandardSideMargins ? { paddingHorizontal: 20 } : {}, // Ensures horizontal margins are consistently set
        ]}
      >
        <StatusBar style={!dark ? "dark" : "light"} />
        {headerSticky && <View className="flex-1">{children}</View>}
        <View
          testID={"header"}
          className={cn(
            headerSticky ? `absolute inset-x-0 top-0` : `w-full relative`,
            headerClassName
          )}
          style={{
            paddingTop: resolvedTopPadding,
          }}
        >
          {header}
        </View>
        {!headerSticky && <View className="flex-1">{children}</View>}
        {!isDesktop && !noBackButton && (
          <BFBackButton
            dark={!dark}
            className={`absolute left-5`}
            style={{ top: insets.top + 16 }}
          />
        )}
      </View>
    </>
  )
}
